import * as THREE from 'three';
import { ROSELoader } from './rose';

/**
 * @property {Boolean} enabled
 * @property {String} name
 * @property {Number} loopCount
 * @property {Number} index
 */
export class AnimationData {
  enabled: boolean = false;
  path: string = '';
  loopCount: number = 0;
  index: number = 0;
}

/**
 * @property {String} name
 * @property {String} uid
 * @property {Number} stbIndex
 * @property {String} particlePath
 * @property {AnimationData} animation
 * @property {THREE.Vector3} position
 * @property {THREE.Quaternion} rotation
 * @property {Number} delay
 * @property {Boolean} linkRoot
 */
export class EffectParticle {
  name: string = '';
  uid: string = '';
  stbIndex: number = 0;
  particlePath: string = '';
  animation: any = {};
  position: any = null;
  rotation: any = null;
  delay: number = 0;
  linkRoot: boolean = false;
}

/**
 * @property {String} name
 * @property {String} uid
 * @property {Number} stbIndex
 * @property {String} meshPath
 * @property {String} animationPath
 * @property {String} texturePath
 * @property {Boolean} alphaEnabled
 * @property {Boolean} twoSideEnabled
 * @property {Boolean} alphaTestEnabled
 * @property {Boolean} depthTestEnabled
 * @property {Boolean} depthWriteEnabled
 * @property {Number} blendSrc
 * @property {Number} blendDst
 * @property {Number} blendOp
 * @property {AnimationData} animation
 * @property {THREE.Vector3} position
 * @property {THREE.Quaternion} rotation
 * @property {Number} delay
 * @property {Number} loopCount
 * @property {Boolean} linkRoot
 */
export class EffectAnimation {
  name: string = '';
  uid: string = '';
  stbIndex: number = 0;
  meshPath: string = '';
  animationPath: string | null = '';
  texturePath: string = '';
  alphaEnabled: boolean = false;
  twoSideEnabled: boolean = false;
  alphaTestEnabled: boolean = false;
  depthTestEnabled: boolean = false;
  depthWriteEnabled: boolean = false;
  blendSrc: number = 0;
  blendDst: number = 0;
  blendOp: number = 0;
  animation: AnimationData = new AnimationData();
  position: any = null;
  rotation: any = null;
  delay: number = 0;
  loopCount: number = 0;
  linkRoot: boolean = false;
}

/*
 +D3DXQUATERNION* WINAPI D3DXQuaternionRotationYawPitchRoll(D3DXQUATERNION *pout, FLOAT yaw, FLOAT pitch, FLOAT roll)
 +{
 +    pout->x = sin( yaw / 2.0f) * cos(pitch / 2.0f) * sin(roll / 2.0f) + cos(yaw / 2.0f) * sin(pitch / 2.0f) * cos(roll / 2.0f);
 +    pout->y = sin( yaw / 2.0f) * cos(pitch / 2.0f) * cos(roll / 2.0f) - cos(yaw / 2.0f) * sin(pitch / 2.0f) * sin(roll / 2.0f);
 +    pout->z = cos(yaw / 2.0f) * cos(pitch / 2.0f) * sin(roll / 2.0f) - sin( yaw / 2.0f) * sin(pitch / 2.0f) * cos(roll / 2.0f);
 +    pout->w = cos( yaw / 2.0f) * cos(pitch / 2.0f) * cos(roll / 2.0f) + sin(yaw / 2.0f) * sin(pitch / 2.0f) * sin(roll / 2.0f);
 +    return pout;
 +}
 */
function D3DXQuaternionRotationYawPitchRoll(yaw: any, pitch: any, roll: any) {
  const x = Math.sin( yaw / 2.0) * Math.cos(pitch / 2.0) * Math.sin(roll / 2.0) + Math.cos(yaw / 2.0) * Math.sin(pitch / 2.0) * Math.cos(roll / 2.0);
  const y = Math.sin( yaw / 2.0) * Math.cos(pitch / 2.0) * Math.cos(roll / 2.0) - Math.cos(yaw / 2.0) * Math.sin(pitch / 2.0) * Math.sin(roll / 2.0);
  const z = Math.cos(yaw / 2.0) * Math.cos(pitch / 2.0) * Math.sin(roll / 2.0) - Math.sin( yaw / 2.0) * Math.sin(pitch / 2.0) * Math.cos(roll / 2.0);
  const w = Math.cos( yaw / 2.0) * Math.cos(pitch / 2.0) * Math.cos(roll / 2.0) + Math.sin(yaw / 2.0) * Math.sin(pitch / 2.0) * Math.sin(roll / 2.0);

  return new THREE.Quaternion(x, y, z, w);
}

/**
 * @property {String} name
 * @property {Boolean} soundEnabled
 * @property {String} soundPath
 * @property {Number} loopCount
 * @property {EffectParticle[]} particles
 * @property {EffectAnimation[]} animations
 */
export class EffectData {
  name: string = '';
  soundEnabled: boolean = false;
  soundPath: string = '';
  soundLoopCount: number = 0;
  particles: EffectParticle[] = [];
  animations: EffectAnimation[] = [];

  /**
   * @param {String} path
   * @param {Function} callback
   */
  static load(path: any, callback: any) {
    ROSELoader.load(path, function(rh: any) {
      let animations, data, i, particles;

      data = new EffectData();
      data.name         = rh.readUint32Str();
      data.soundEnabled = (rh.readUint32() & 0xff) !== 0;
      data.soundPath    = rh.readUint32Str();
      data.soundLoopCount    = rh.readUint32();

      particles = rh.readUint32();
      for (i = 0; i < particles; ++i) {
        const particle = new EffectParticle();
        particle.name                 = rh.readUint32Str();
        particle.uid                  = rh.readUint32Str();
        particle.stbIndex             = rh.readUint32();
        particle.particlePath         = rh.readUint32Str();
        particle.animation.enabled    = rh.readUint32() !== 0;
        particle.animation.path       = rh.readUint32Str();
        particle.animation.loopCount  = rh.readUint32();
        particle.animation.index      = rh.readUint32();
        particle.position             = rh.readVector3();
        particle.rotation             = rh.readQuat();
        particle.rotation = D3DXQuaternionRotationYawPitchRoll(particle.rotation.x, particle.rotation.y, particle.rotation.z);
        particle.delay                = rh.readUint32();
        particle.linkRoot             = rh.readUint32() !== 0;
        particle.position.multiplyScalar(ZZ_SCALE_IN);
        if (!particle.animation.path || particle.animation.path === 'NULL') {
          particle.animation.enabled = false;
        }
        data.particles.push(particle);
      }

      animations = rh.readUint32();
      for (i = 0; i < animations; ++i) {
        const animation = new EffectAnimation();
        animation.name                = rh.readUint32Str();
        animation.uid                 = rh.readUint32Str();
        animation.stbIndex            = rh.readUint32();
        animation.meshPath            = rh.readUint32Str();
        animation.animationPath       = rh.readUint32Str();
        animation.texturePath         = rh.readUint32Str();
        animation.alphaEnabled        = rh.readUint32() !== 0;
        animation.twoSideEnabled      = rh.readUint32() !== 0;
        animation.alphaTestEnabled    = rh.readUint32() !== 0;
        animation.depthTestEnabled    = rh.readUint32() !== 0;
        animation.depthWriteEnabled   = rh.readUint32() !== 0;
        animation.blendSrc            = rh.readUint32();
        animation.blendDst            = rh.readUint32();
        animation.blendOp             = rh.readUint32();
        animation.animation.enabled   = rh.readUint32() !== 0;
        animation.animation.path      = rh.readUint32Str();
        animation.animation.loopCount = rh.readUint32();
        animation.animation.index     = rh.readUint32();
        animation.position            = rh.readVector3();
        animation.rotation            = rh.readQuat();
        animation.rotation = D3DXQuaternionRotationYawPitchRoll(animation.rotation.x, animation.rotation.y, animation.rotation.z);
        animation.delay               = rh.readUint32();
        animation.loopCount           = rh.readUint32();
        animation.linkRoot            = rh.readUint32() !== 0;
        animation.position.multiplyScalar(ZZ_SCALE_IN);
        if (animation.animationPath === 'NULL') {
          animation.animationPath = null;
        }
        if (!animation.animation.path || animation.animation.path === 'NULL') {
          animation.animation.path = null;
          animation.animation.enabled = false;
        }
        data.animations.push(animation);
      }

      callback(data);
    });
  }
}
